/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {makeStyles} from '@material-ui/core';
import {translate} from '@ion/core/src/locale/locale';
import {Entity} from '@ion/core/src/types/entities';
import {ButtonSpinner} from '../components/loading/ButtonSpinner';
import {UserNotificationDialog} from '../notifications/UserNotificationDialog';

export interface DeletePromptType {
  entity: Entity | string;
  items: object | object[];
  isLoading?: boolean;
  overrideMessage?: Function | null;
  onConfirm: () => void;
  onCancel: () => void;
  show: boolean;
  bulkDelete?: boolean;
  singleDeleteMessage?: boolean;
  titleMessage?: boolean;
  isCancel?: boolean;
  allowWhitespace?: boolean;
}

const useStyles = makeStyles(theme => ({
  actionLabel : {
    minWidth : '120px'
  }
}));

/**
 *
 * @DeletePrompt
 *
 * @param {boolean} show determines if the dialog should be shown
 * @param {Entity | string} entity entity type that is being deleted
 * @param {object | Array<object>} items items(s) that are to be deleted
 * @param {Function} onConfirm callback function that gets invoked when user
 *   clicks on action cta button
 * @param {Function} onCancel callback function that gets invoked when user
 *   clicks on dismiss cta button
 * @param {boolean} isLoading entity type that is being deleted
 *
 * ```typescript
 *
 * import {DeletePrompt} from '@ion/dls/src/editors
 *
 * <DeletePrompt show={show}
 *   entity={Entity.asset}
 *   items={itemsList}
 *   onConfirm={onCompletedDelete}
 *   onCancel={onCanceledDelete}
 *   isLoading={deleteWorking} />
 *
 * ```
 */
export const DeletePrompt = ({
  show,
  entity,
  items,
  onConfirm,
  onCancel,
  allowWhitespace,
  overrideMessage = null,
  isLoading = false,
  bulkDelete = false,
  singleDeleteMessage = false,
  titleMessage = false,
  isCancel = false
}: DeletePromptType) => {
  const classes = useStyles();

  function getMessage(): string {
    if (!overrideMessage) {
      const pluralizedMessage = bulkDelete ? translate('common:editors.delete.items').toLowerCase() : entity;
      return translate('common:editors.delete.message', {message: pluralizedMessage, count: getItemsLength()});
    }
    return overrideMessage();
  }

  function getSingleMessage(): string {
    if (!overrideMessage) {
      return translate('common:editors.delete.singleDeleteMessage', {entity});
    }
    return overrideMessage();
  }

  function getItemsLength() {
    if (!Array.isArray(items)) {
      return 1;
    }
    return items.length;
  }

  return (
    <UserNotificationDialog show={show}
      capitalize
      allowWhitespace={allowWhitespace}
      title={translate(`common:editors.delete.${titleMessage ? 'emptyTitle' : 'title'}`, {entity})}
      message={singleDeleteMessage ? getSingleMessage() : getMessage()}
      actionLabel={
        <span className={classes.actionLabel}>
          {
            !isLoading ? (
              translate(`${isCancel ? 'downloadjobs' : 'common:editors'}:delete.confirm`)
            ) : <ButtonSpinner />
          }
        </span>
      }
      dismissLabel={translate('common:editors.delete.cancel')}
      onAction={onConfirm}
      onDismiss={onCancel} />
  );
};

export default DeletePrompt;
